/**
 * ShortAnswerRenderer — renders a `short_answer` artifact (1-3 paragraph
 * direct response to a question).
 *
 * Wraps `<ArtifactFrame>` and renders the markdown body via the canonical
 * `MarkdownRenderer`. Compared to `DocumentRenderer` this is intentionally
 * more compact (smaller inner padding, no derived subtitle) — the artifact
 * is meant to feel like a quick reply card rather than a memo.
 *
 * Spec: .workspace-spec/WORKSPACE_CONTRACT.md
 */
import { useMemo } from "react"

import { useTranslation } from "react-i18next"

import type { Artifact, ArtifactAction, ShortAnswerPayload } from "@/api/models"

import { ArtifactFrame } from "./ArtifactFrame"
import { WorkspaceMarkdownRenderer } from "./WorkspaceMarkdownRenderer"

export interface ShortAnswerRendererProps {
  artifact: Artifact & { payload: ShortAnswerPayload }
  versionChain?: Artifact[]
  onAction?: (actionId: string, artifact: Artifact) => void
  onSelectVersion?: (artifactId: string) => void
  onViewTrace?: (missionId: string) => void
  onShowHistory?: () => void
  citationSection?: string
}

export function ShortAnswerRenderer({
  artifact,
  versionChain,
  onAction,
  onSelectVersion,
  onViewTrace,
  onShowHistory,
  citationSection,
}: ShortAnswerRendererProps) {
  const { t } = useTranslation()
  const framedArtifact = useMemo<Artifact>(() => {
    const hasActions = (artifact.actions ?? []).length > 0
    const actions: ArtifactAction[] = hasActions
      ? (artifact.actions ?? [])
      : [{ id: "copy", label: t("deepExplorer.artifact_actions.copy"), icon: "copy" }]
    return { ...artifact, actions }
  }, [artifact, t])

  return (
    <ArtifactFrame
      artifact={framedArtifact}
      className="short-answer-card"
      versionChain={versionChain}
      onAction={onAction}
      onSelectVersion={onSelectVersion}
      onShowHistory={onShowHistory}
      onViewTrace={onViewTrace}
    >
      <div className="py-1 text-sm" data-testid="short-answer-renderer-body">
        <WorkspaceMarkdownRenderer
          citationSection={citationSection}
          content={artifact.payload.body}
        />
      </div>
    </ArtifactFrame>
  )
}
